/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties, useState} from "react";
import {
    I18n,
    SimpleDatePicker
} from "simple-ts-date-picker";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {BadImplementationException} from "@intuitionrobotics/ts-common";

type Props = {
    onSchedule: (timestamp: number | undefined) => void
    minDate?: Date
    initialTimestamp?: number
}

type Period = "AM" | "PM";

const style: CSSProperties = {
    textAlign: 'center',
    fontWeight: "bold",
    backgroundColor: COLORS.white,
    color: COLORS.grays_dark
};
const arrowStyle = {
    color: COLORS.grays_4,
    backgroundColor: COLORS.white,
    cursor: 'pointer'
};

const timeFieldStyle: CSSProperties = {
    padding: "4px 6px",
    border: `1px solid ${COLORS.neutral_gray_2}`,
    borderRadius: 4,
    fontSize: 14,
    width: 36,
    textAlign: "center"
};

const periodFieldStyle: CSSProperties = {
    padding: "4px 6px",
    border: `1px solid ${COLORS.neutral_gray_2}`,
    borderRadius: 4,
    fontSize: 14
};

const splitInitialTime = (timestamp?: number): {hours: string, minutes: string, period: Period} => {
    if (!timestamp)
        return {hours: "", minutes: "", period: "AM"};
    const d = new Date(timestamp);
    const h24 = d.getHours();
    const period: Period = h24 >= 12 ? "PM" : "AM";
    const h12 = ((h24 + 11) % 12) + 1;
    return {
        hours: String(h12).padStart(2, "0"),
        minutes: String(d.getMinutes()).padStart(2, "0"),
        period
    };
};

const toTime24 = (hoursStr: string, minutesStr: string, period: Period): {h: number, m: number} | undefined => {
    const h = parseInt(hoursStr, 10);
    const m = parseInt(minutesStr, 10);
    if (isNaN(h) || isNaN(m) || h < 1 || h > 12 || m < 0 || m > 59)
        return undefined;
    let h24 = h % 12;
    if (period === "PM") h24 += 12;
    return {h: h24, m};
};

export const ScheduleDateTimePicker = (props: Props) => {
    const now = new Date();
    const monthsArray = ['Jan', 'Feb', 'March', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

    const getInitialDate = () => props.initialTimestamp ? new Date(props.initialTimestamp) : undefined;

    const initialDate = getInitialDate();
    const initialTime = splitInitialTime(props.initialTimestamp);
    const [dateValue, setDateValue] = useState<Date | undefined>(initialDate);
    const [month, setMonth] = useState({
        name: monthsArray[(initialDate || now).getMonth()],
        index: (initialDate || now).getMonth()
    });
    const [year, setYear] = useState((initialDate || now).getFullYear());
    const [hours, setHours] = useState(initialTime.hours);
    const [minutes, setMinutes] = useState(initialTime.minutes);
    const [period, setPeriod] = useState<Period>(initialTime.period);

    const minDate = props.minDate || new Date();
    minDate.setHours(0, 0, 0, 0);

    const combineAndNotify = (date: Date | undefined, h: string, m: string, p: Period) => {
        if (!date) {
            props.onSchedule(undefined);
            return;
        }

        const time = toTime24(h, m, p);
        if (!time) {
            props.onSchedule(undefined);
            return;
        }

        const combined = new Date(date);
        combined.setHours(time.h, time.m, 0, 0);

        if (combined.getTime() <= Date.now()) {
            props.onSchedule(undefined);
            return;
        }

        props.onSchedule(combined.getTime());
    };

    const getClickedStyle = (day: Date) => {
        let color = COLORS.grays_dark;
        const isSelected = dateValue && dateValue.getDate() === day.getDate() && dateValue.getMonth() === day.getMonth() && dateValue.getFullYear() === day.getFullYear();

        if (day < minDate)
            color = COLORS.grays_2;
        if (isSelected)
            color = 'white';

        const cellStyle = {
            background: isSelected ? COLORS.semantic_blue : 'white',
            color,
            cursor: day < minDate ? 'default' : 'pointer'
        };
        return <td key={day.getTime()} onClick={() => {
            if (day >= minDate) {
                setDateValue(day);
                combineAndNotify(day, hours, minutes, period);
            }
        }} style={cellStyle}>{day.getDate()}</td>;
    };

    const calculatePreviousMonth = (days: I18n) => {
        if (month.index === -1)
            throw new BadImplementationException('you cant have a month in state that does not exist in months list');
        if (month.index === 0)
            return days.months[11];
        return days.months[month.index - 1];
    };

    const calculateNextMonth = (days: I18n) => {
        if (month.index === -1)
            throw new BadImplementationException('you cant have a month in state that does not exist in months list');
        if (month.index === days.months.length - 1)
            return days.months[0];
        return days.months[month.index + 1];
    };

    const renderCalHeader = (days: I18n, date: Date, onScreenChange: (screen: Date) => void) => {
        return <tr>
            <th style={arrowStyle} onClick={() => {
                const previousMonth = calculatePreviousMonth(days);
                setMonth({name: previousMonth, index: days.months.indexOf(previousMonth)});
                onScreenChange(new Date(date.getFullYear(), days.months.indexOf(previousMonth)));
            }}>
                <a>&#8249;</a>
            </th>
            <th style={style}>{month.name}</th>
            <th style={arrowStyle} onClick={() => {
                const nextMonth = calculateNextMonth(days);
                setMonth({name: nextMonth, index: days.months.indexOf(nextMonth)});
                onScreenChange(new Date(date.getFullYear(), days.months.indexOf(nextMonth)));
            }}>
                <a>&#8250;</a>
            </th>
            <th style={arrowStyle} onClick={() => {
                const previousYear = year - 1;
                setYear(previousYear);
                onScreenChange(new Date(previousYear, month.index));
            }}>
                <a>&#8249;</a>
            </th>
            <th style={style}>{year}</th>
            <th style={arrowStyle} onClick={() => {
                const nextYear = year + 1;
                setYear(nextYear);
                onScreenChange(new Date(nextYear, month.index));
            }}>
                <a>&#8250;</a>
            </th>
        </tr>;
    };

    const renderDayAbbrev = (days: I18n) => {
        return <tr>
            {days.weekdaysShort.map((_day, i) => <th key={i} style={{color: COLORS.semantic_blue}}>{_day}</th>)}
        </tr>;
    };

    const onClear = () => {
        setDateValue(undefined);
        setHours("");
        setMinutes("");
        setPeriod("AM");
        props.onSchedule(undefined);
    };

    const onHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 2);
        if (value !== "") {
            const n = parseInt(value, 10);
            if (n < 0 || n > 12)
                return;
        }
        setHours(value);
        combineAndNotify(dateValue, value, minutes, period);
    };

    const onHoursBlur = () => {
        const h = parseInt(hours, 10);
        if (!isNaN(h) && h >= 1 && h <= 12)
            setHours(String(h).padStart(2, "0"));
    };

    const onMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 2);
        if (value !== "") {
            const n = parseInt(value, 10);
            if (n < 0 || n > 59)
                return;
        }
        setMinutes(value);
        combineAndNotify(dateValue, hours, value, period);
    };

    const onMinutesBlur = () => {
        const m = parseInt(minutes, 10);
        if (!isNaN(m) && m >= 0 && m <= 59)
            setMinutes(String(m).padStart(2, "0"));
    };

    const onPeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const next = e.target.value as Period;
        setPeriod(next);
        combineAndNotify(dateValue, hours, minutes, next);
    };

    return <div style={{padding: "10px 20px"}}>
        {FONTS.medium_12("Schedule send", {marginBottom: 8, color: COLORS.grays_dark})}
        <SimpleDatePicker
            value={dateValue || now}
            renderDay={day => getClickedStyle(day)}
            renderAbbreviations={day => renderDayAbbrev(day)}
            renderHeader={(day, date, onScreenChange) => renderCalHeader(day, date, onScreenChange)}
            onChange={(value) => setDateValue(value)}/>
        <div style={{display: "flex", alignItems: "center", marginTop: 10, gap: 8}}>
            {FONTS.reg_14("Time:", {color: COLORS.grays_dark})}
            <input
                type="text"
                inputMode="numeric"
                placeholder="hh"
                value={hours}
                onChange={onHoursChange}
                onBlur={onHoursBlur}
                onFocus={(e) => e.target.select()}
                style={timeFieldStyle}
            />
            <span style={{color: COLORS.grays_dark}}>:</span>
            <input
                type="text"
                inputMode="numeric"
                placeholder="mm"
                value={minutes}
                onChange={onMinutesChange}
                onBlur={onMinutesBlur}
                onFocus={(e) => e.target.select()}
                style={timeFieldStyle}
            />
            <select value={period} onChange={onPeriodChange} style={periodFieldStyle}>
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
            <div className={"clickable"} onClick={onClear}
                 style={{marginLeft: "auto"}}>
                {FONTS.reg_14("Clear", {color: COLORS.semantic_blue, textDecoration: "underline"})}
            </div>
        </div>
    </div>;
};
